import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-quote-compare',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './quote-compare.component.html',
  styleUrls: ['./quote-compare.component.css']
})
export class QuoteCompareComponent implements OnInit {
  product: any = null;
  quotes: any[] = [];
  comparisonQuantities: any[] = [];
  comparisonSuppliers: any[] = [];
  loading = false;
  pricingView: 'unit' | 'total' = 'unit'; // 'unit' for Per Unit Price, 'total' for Total Price
  showPricingDropdown = false;
  validityDateFilter: 'all' | 'ascending' | 'descending' = 'all';
  showValidityDateDropdown = false;
  searchQuery: string = '';
  allSuppliers: any[] = []; // Store all suppliers for filtering

  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    // Get data from route state (using history.state as fallback)
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || (window.history.state && window.history.state.navigationExtras?.state) || window.history.state;
    
    if (state && (state['product'] || state['quotes'])) {
      this.product = state['product'];
      this.quotes = state['quotes'] || [];
      this.prepareComparisonData();
    } else {
      // Try to get from sessionStorage as fallback
      const storedData = sessionStorage.getItem('quoteCompareData');
      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          this.product = data.product;
          this.quotes = data.quotes || [];
          this.prepareComparisonData();
          sessionStorage.removeItem('quoteCompareData');
        } catch (e) {
          console.error('Error parsing stored data:', e);
          this.router.navigate(['/product']);
        }
      } else {
        // Fallback: redirect back to products
        this.router.navigate(['/product']);
      }
    }
  }

  prepareComparisonData() {
    if (!this.quotes || this.quotes.length === 0) {
      return;
    }

    // Collect all unique quantities from all quotes
    const quantityMap = new Map<string, { quantity: number; uom: string }>();
    
    this.quotes.forEach(quote => {
      if (quote.quantityPrices && Array.isArray(quote.quantityPrices)) {
        quote.quantityPrices.forEach((qp: any) => {
          if (qp.quantity && qp.uom && qp.unitPrice && qp.unitPrice > 0) {
            const key = `${qp.quantity}_${qp.uom}`;
            if (!quantityMap.has(key)) {
              quantityMap.set(key, { quantity: qp.quantity, uom: qp.uom });
            }
          }
        });
      }
    });
    
    // Convert to array and sort by quantity
    this.comparisonQuantities = Array.from(quantityMap.values())
      .sort((a, b) => a.quantity - b.quantity);
    
    // Prepare supplier data
    let suppliersData = this.quotes.map(quote => ({
      quoteId: quote.quoteId,
      supplierId: quote.supplier?._id,
      supplierName: quote.supplier?.companyName || quote.supplier?.name || 'Unknown',
      supplierIdCode: quote.supplier?.supplierId || '',
      supplierLocation: this.getSupplierLocation(quote),
      currency: quote.currency || 'INR',
      status: quote.supplierValidDateStatus || quote.status,
      submissionDate: quote.submittedAt || quote.submittedOn,
      validityDate: quote.validityDate || quote.validUpto,
      quote: quote,
      prices: new Map<string, { unitPrice: number; totalPrice: number }>()
    }));
    
    // Populate prices for each supplier
    suppliersData.forEach((supplier, index) => {
      const quote = this.quotes[index];
      if (quote.quantityPrices && Array.isArray(quote.quantityPrices)) {
        quote.quantityPrices.forEach((qp: any) => {
          if (qp.quantity && qp.uom && qp.unitPrice && qp.unitPrice > 0) {
            const key = `${qp.quantity}_${qp.uom}`;
            supplier.prices.set(key, {
              unitPrice: qp.unitPrice || 0,
              totalPrice: qp.totalPrice || (qp.quantity * qp.unitPrice) || 0
            });
          }
        });
      }
    });
    
    // Apply validity date filter/sort
    this.allSuppliers = this.applyValidityDateFilter(suppliersData);
    // Apply search filter
    this.applySearchFilter();
  }

  applySearchFilter() {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.comparisonSuppliers = [...this.allSuppliers];
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.comparisonSuppliers = this.allSuppliers.filter(supplier => {
      // Search by Quote ID
      const quoteIdMatch = supplier.quoteId?.toLowerCase().includes(query);
      
      // Search by Supplier Name
      const supplierNameMatch = supplier.supplierName?.toLowerCase().includes(query);
      
      // Search by Supplier ID
      const supplierIdMatch = supplier.supplierIdCode?.toLowerCase().includes(query);
      
      return quoteIdMatch || supplierNameMatch || supplierIdMatch;
    });
  }

  onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value;
    this.applySearchFilter();
  }

  getSupplierLocation(quote: any): string {
    const supplier = quote.supplier;
    if (!supplier) return '';
    
    const parts = [];
    if (supplier.city) parts.push(supplier.city);
    if (supplier.state) parts.push(supplier.state);
    if (supplier.country) parts.push(supplier.country);
    
    return parts.length > 0 ? parts.join(', ') : '';
  }

  getPriceForQuantity(supplier: any, quantity: number, uom: string): { unitPrice: number; totalPrice: number } | null {
    const key = `${quantity}_${uom}`;
    return supplier.prices.get(key) || null;
  }

  formatQuantity(quantity: number, uom: string): string {
    return `${this.formatNumber(quantity)} ${uom}`;
  }

  formatNumber(num: number): string {
    if (!num) return '0';
    return num.toLocaleString('en-IN');
  }

  formatDate(date: any): string {
    if (!date) return '-';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  getStatusClass(status: string): string {
    if (status === 'Active') return 'status-active';
    if (status === 'Expired') return 'status-expired';
    return '';
  }

  getStatusText(status: string): string {
    if (status === 'Active') return 'Active';
    if (status === 'Expired') return 'Expired';
    return status || '-';
  }

  goBack() {
    // Navigate back to the product view if we have product ID
    if (this.product?._id) {
      this.router.navigate(['/product/view', this.product._id], {
        queryParams: { tab: 'quote' }
      });
    } else {
      this.router.navigate(['/product']);
    }
  }

  printPage() {
    // Add printing class to body to trigger print styles
    document.body.classList.add('printing');
    
    // Handle print event cleanup
    const handleAfterPrint = () => {
      document.body.classList.remove('printing');
      window.removeEventListener('afterprint', handleAfterPrint);
    };
    
    // Use afterprint event if available, otherwise fallback to setTimeout
    if ('onafterprint' in window) {
      window.addEventListener('afterprint', handleAfterPrint);
    } else {
      // Fallback for browsers that don't support afterprint
      setTimeout(() => {
        handleAfterPrint();
      }, 500);
    }
    
    // Trigger print dialog
    window.print();
  }

  onPricingViewChange() {
    // This method is called when the pricing view changes
    // The view will automatically update due to Angular's change detection
  }

  togglePricingDropdown() {
    this.showPricingDropdown = !this.showPricingDropdown;
  }

  selectPricingView(view: 'unit' | 'total') {
    this.pricingView = view;
    this.showPricingDropdown = false;
  }

  applyValidityDateFilter(suppliers: any[]): any[] {
    if (this.validityDateFilter === 'all') {
      return suppliers;
    }
    
    // Sort by validityDate
    // Quotes without validityDate should go to the end
    const sorted = [...suppliers].sort((a, b) => {
      const dateA = a.validityDate ? new Date(a.validityDate).getTime() : Number.MAX_SAFE_INTEGER;
      const dateB = b.validityDate ? new Date(b.validityDate).getTime() : Number.MAX_SAFE_INTEGER;
      
      if (this.validityDateFilter === 'ascending') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
    
    return sorted;
  }

  toggleValidityDateDropdown() {
    this.showValidityDateDropdown = !this.showValidityDateDropdown;
  }

  selectValidityDateFilter(filter: 'all' | 'ascending' | 'descending') {
    this.validityDateFilter = filter;
    this.showValidityDateDropdown = false;
    // Re-apply filter to suppliers by rebuilding from original quotes
    let suppliersData = this.quotes.map(quote => ({
      quoteId: quote.quoteId,
      supplierId: quote.supplier?._id,
      supplierName: quote.supplier?.companyName || quote.supplier?.name || 'Unknown',
      supplierIdCode: quote.supplier?.supplierId || '',
      supplierLocation: this.getSupplierLocation(quote),
      currency: quote.currency || 'INR',
      status: quote.supplierValidDateStatus || quote.status,
      submissionDate: quote.submittedAt || quote.submittedOn,
      validityDate: quote.validityDate || quote.validUpto,
      quote: quote,
      prices: new Map<string, { unitPrice: number; totalPrice: number }>()
    }));
    
    // Populate prices for each supplier
    suppliersData.forEach((supplier, index) => {
      const quote = this.quotes[index];
      if (quote.quantityPrices && Array.isArray(quote.quantityPrices)) {
        quote.quantityPrices.forEach((qp: any) => {
          if (qp.quantity && qp.uom && qp.unitPrice && qp.unitPrice > 0) {
            const key = `${qp.quantity}_${qp.uom}`;
            supplier.prices.set(key, {
              unitPrice: qp.unitPrice || 0,
              totalPrice: qp.totalPrice || (qp.quantity * qp.unitPrice) || 0
            });
          }
        });
      }
    });
    
    this.allSuppliers = this.applyValidityDateFilter(suppliersData);
    // Apply search filter
    this.applySearchFilter();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.pricing-view-dropdown')) {
      this.showPricingDropdown = false;
    }
    if (!target.closest('.validity-date-dropdown')) {
      this.showValidityDateDropdown = false;
    }
  }
}
